import { useMemo, useState } from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { Pill } from "@/components/primitives";
import { useReferenceTargetCatalog } from "@/hooks/useReferenceTargetCatalog";
import type { FamilyKey, Recommendation } from "@/utils/types/flavor";
import { LockPulse } from "./LockPulse";

const EMPTY_RECS: Recommendation[] = [];

type CatalogModeProps = {
  pickedRec: Recommendation | null;
  onPick: (rec: Recommendation | null) => void;
  baseFamily: FamilyKey | null;
  reRankPulse: boolean;
};

export function CatalogMode({
  pickedRec,
  onPick,
  baseFamily,
  reRankPulse,
}: CatalogModeProps) {
  const [search, setSearch] = useState("");
  const [lockedPulse, setLockedPulse] = useState<{
    id: string;
    reason: string;
  } | null>(null);

  const catalog = useReferenceTargetCatalog();
  const allRecs = catalog.data ?? EMPTY_RECS;

  const recsRanked = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRecs;
    return allRecs.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.note.toLowerCase().includes(q) ||
        (r.categoryTag ?? r.family).toLowerCase().includes(q)
    );
  }, [allRecs, search]);

  const matchingFamilyCount = baseFamily
    ? recsRanked.filter(
        (r) => (r.categoryTag ?? r.family) === baseFamily && !r.locked,
      ).length
    : 0;

  return (
    <div>
      <div
        className="hstack"
        style={{
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div
          className="hstack"
          style={{ gap: 10, flexWrap: "wrap", alignItems: "baseline" }}
        >
          <div className="label">Which of these is closest to what you want?</div>
          {baseFamily && (
            <span
              className={cn("rerank-chip", reRankPulse && "pulsing")}
              title={`Templates whose family matches the base formula (${baseFamily}) are listed first.`}
            >
              <Icon name="zap" size={10} />
              Recommended based on fixed ingredients
              <span className="mono" style={{ opacity: 0.7 }}>
                · {matchingFamilyCount} match
              </span>
            </span>
          )}
        </div>
        <div className="mono" style={{ fontSize: 11, color: "var(--ink-4)" }}>
          {recsRanked.length} of {allRecs.length}
        </div>
      </div>

      <div
        className="hstack"
        style={{
          gap: 8,
          padding: "8px 10px",
          border: "1px solid var(--line)",
          borderRadius: 6,
          background: "var(--bg)",
          marginBottom: 12,
        }}
      >
        <Icon name="search" size={12} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search flavors by name, note, or family…"
          style={{
            flex: 1,
            background: "transparent",
            border: 0,
            fontSize: 13,
            outline: "none",
          }}
        />
        {search && (
          <button
            type="button"
            className="btn xs ghost"
            onClick={() => setSearch("")}
            style={{ padding: "2px 4px" }}
          >
            <Icon name="x" size={10} />
          </button>
        )}
      </div>

      {catalog.isPending && allRecs.length === 0 ? (
        <div
          style={{
            padding: "28px 16px",
            textAlign: "center",
            color: "var(--ink-4)",
            fontSize: 13,
          }}
        >
          Loading catalog…
        </div>
      ) : catalog.isError ? (
        <div
          style={{
            padding: "28px 16px",
            textAlign: "center",
            color: "var(--ink-4)",
            fontSize: 13,
          }}
        >
          Couldn't load the catalog. Try again later.
        </div>
      ) : recsRanked.length === 0 ? (
        <div
          style={{
            padding: "28px 16px",
            textAlign: "center",
            color: "var(--ink-4)",
            fontSize: 13,
          }}
        >
          {search ? (
            <>
              No flavors matched <span className="mono">"{search}"</span>. Try a
              broader term.
            </>
          ) : (
            <>The catalog is empty.</>
          )}
        </div>
      ) : (
        <div className="rec-grid-scroll">
          <div className="rec-grid">
            {recsRanked.map((r) => {
              const locked = !!r.locked;
              const selected = pickedRec?.id === r.id;
              const pulsing = lockedPulse?.id === r.id;
              const matchBase =
                !!baseFamily &&
                (r.categoryTag ?? r.family) === baseFamily &&
                !locked;
              return (
                <button
                  key={r.id}
                  type="button"
                  className={cn(
                    "rec-card",
                    locked && "locked",
                    selected && !locked && "selected"
                  )}
                  onClick={() => {
                    if (locked) {
                      setLockedPulse({
                        id: r.id,
                        reason: r.lockReason || "Locked",
                      });
                      return;
                    }
                    onPick(pickedRec?.id === r.id ? null : r);
                  }}
                >
                  <div
                    className="hstack"
                    style={{ justifyContent: "space-between", marginBottom: 8 }}
                  >
                    <div className="hstack" style={{ gap: 6 }}>
                      <Pill
                        family={r.categoryTag ?? r.family}
                        color={r.categoryColor ?? undefined}
                      />
                      {matchBase && <span className="rec-best-badge">Best fit</span>}
                    </div>
                    {selected && !locked && <Icon name="check" size={14} />}
                    {locked && (
                      <span style={{ color: "var(--ink-4)", display: "inline-flex" }}>
                        <Icon name="lock" size={12} />
                      </span>
                    )}
                  </div>
                  <div className="rec-card-name">{r.name}</div>
                  <div className="rec-card-note">{r.note}</div>
                  {locked && pulsing && lockedPulse && (
                    <LockPulse
                      reason={lockedPulse.reason}
                      onDone={() => setLockedPulse(null)}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
